import { useMemo } from 'react';

import { PartLocationTypeCode, StructureTypeCategory } from '../../../configs/enums';
import { useGetStructureTypesByCategoryCode } from '../../../queries/structure-type-query';
import { SIBLING_NODE_TYPE_NAME, displayNodeTypeName } from '../../../utils/node-type';

type FilterListOption = { label: string; value: string };

const LOCATION_TYPE_CODE_OPTIONS: FilterListOption[] = [
  { label: 'Node', value: PartLocationTypeCode.Node },
  { label: 'Part', value: PartLocationTypeCode.Part },
];

/**
 * Synthetic Node Type options for locations the operational structure-type
 * query can't return, but where parts genuinely live:
 *
 * - `'Part'` — a part installed inside another part has no structure node, so
 *   the `partlist` view reports its Node Type as the literal string `'Part'`
 *   (`nodeTypeName = CASE WHEN "locationTypeCode" = 'part' THEN 'Part' ELSE
 *   "nodeType"."name" END`). Case-sensitive: the value must be exactly `'Part'`.
 * - `'User'` — the technician/user node type is the `Sibling` structure type,
 *   which lives in the `system` category and so is never returned by the
 *   operational query. Stored `nodeTypeName` is `Sibling`; it's displayed as
 *   "User" via {@link displayNodeTypeName}, so the filter value must be
 *   `Sibling`, not `User`.
 *
 * Both are matched by the BFF on the existing `nodeTypeName` wire contract, so
 * no backend change is required.
 */
const SYNTHETIC_NODE_TYPE_OPTIONS: FilterListOption[] = [
  { label: 'Part', value: 'Part' },
  { label: displayNodeTypeName(SIBLING_NODE_TYPE_NAME), value: SIBLING_NODE_TYPE_NAME },
];

/**
 * Provides option sources for the Parts list Add Filter modal's `type: 'list'`
 * fields. Keyed by `FilterFieldDef.key` and consumed via
 * `FilterBar` → `fieldConfig.listOptions`, so each entry renders as an
 * Autocomplete instead of the legacy comma-separated text fallback.
 *
 * Currently wired:
 * - `nodeTypeName` — the part's location-node structure type, sourced from
 *   active operational structure types (the same set used by the Structures
 *   list and the Add Structure Location screen), plus the synthetic "Part" and
 *   "User" options (see {@link SYNTHETIC_NODE_TYPE_OPTIONS}).
 * - `locationTypeCode` — whether the part lives at a node or inside another
 *   part; the two values come from `PartLocationTypeCode`.
 */
export const usePartFilterListOptions = (): Record<string, FilterListOption[]> => {
  const structureTypesQuery = useGetStructureTypesByCategoryCode(StructureTypeCategory.Operational);

  return useMemo(() => {
    const nodeTypeName: FilterListOption[] = (structureTypesQuery.data ?? [])
      .filter((type) => type.isActive)
      .map((type) => ({ label: displayNodeTypeName(type.name), value: type.name }));

    // Add each synthetic option unless the backend already returns it (guards
    // against a future recategorisation), then alphabetise the whole list.
    for (const option of SYNTHETIC_NODE_TYPE_OPTIONS) {
      if (!nodeTypeName.some((existing) => existing.value === option.value)) {
        nodeTypeName.push(option);
      }
    }
    nodeTypeName.sort((a, b) => a.label.localeCompare(b.label));

    return {
      nodeTypeName,
      locationTypeCode: LOCATION_TYPE_CODE_OPTIONS,
    };
  }, [structureTypesQuery.data]);
};
